import React, { useEffect, useRef, useState } from 'react'
import { Loader2, Palette, Save } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { apiErrorMessage, communicationsService } from '../../services/communicationsService'
import type { EmailTemplate, Locale, MailBranding, TemplatePreview } from '../../types/communications'

interface Props {
  notify?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}

const EMPTY: MailBranding = {
  appName: 'Sini Fleet',
  logoUrl: '',
  accentColor: '#0f766e',
  theme: 'LIGHT',
  footerNote: '',
  postalAddress: '',
  supportEmail: ''
}

const PALETTE = ['#0f766e', '#1d4ed8', '#7c3aed', '#b91c1c', '#c2410c', '#0f172a']

const MailBrandingPanel: React.FC<Props> = ({ notify }) => {
  const { t, i18n } = useTranslation()
  const locale = (i18n.language?.startsWith('fr') ? 'fr' : 'en') as Locale
  const [branding, setBranding] = useState<MailBranding>(EMPTY)
  const [sample, setSample] = useState<EmailTemplate | null>(null)
  const [preview, setPreview] = useState<TemplatePreview | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    Promise.all([communicationsService.getBranding(), communicationsService.listTemplates()])
      .then(([saved, catalog]) => {
        setBranding({ ...EMPTY, ...saved })
        // The welcome message exercises the logo, accent, button and footer at once.
        setSample(catalog.templates.find(row => row.templateKey === 'welcome' && row.locale === locale)
          || catalog.templates[0] || null)
      })
      .catch(error => notify?.(apiErrorMessage(error, t('communications.branding.loadFailed')), 'error'))
      .finally(() => setLoading(false))
  }, [])

  // Every change is re-rendered by the server so the preview is the real e-mail.
  const timer = useRef<number>()
  useEffect(() => {
    if (!sample) return
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => {
      communicationsService.previewTemplate(sample, undefined, branding)
        .then(setPreview)
        .catch(() => setPreview(null))
    }, 400)
    return () => window.clearTimeout(timer.current)
  }, [branding, sample])

  const update = <K extends keyof MailBranding>(key: K, value: MailBranding[K]) =>
    setBranding(current => ({ ...current, [key]: value }))

  const save = async () => {
    setSaving(true)
    try {
      setBranding({ ...EMPTY, ...await communicationsService.saveBranding(branding) })
      notify?.(t('communications.branding.saved'), 'success')
    } catch (error) {
      notify?.(apiErrorMessage(error, t('communications.branding.saveFailed')), 'error')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="flex h-48 items-center justify-center"><Loader2 className="animate-spin text-blue-600" /></div>

  const field = (label: string, hint: string, node: React.ReactNode) =>
    <label className="block rounded-xl border border-gray-200 bg-white p-3 text-xs font-medium text-gray-600">
      {label}
      {node}
      <span className="mt-1 block text-[10px] font-normal text-gray-500">{hint}</span>
    </label>

  const inputClass = 'mt-1.5 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500'

  return <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,440px)]">
    <div className="space-y-3">
      <div className="flex items-center gap-2 rounded-xl border border-blue-100 bg-blue-50 p-3">
        <Palette size={16} className="text-blue-600" />
        <p className="text-[11px] text-slate-700">{t('communications.branding.intro')}</p>
      </div>

      {field(t('communications.branding.appName'), t('communications.branding.appNameHint'),
        <input value={branding.appName} onChange={event => update('appName', event.target.value)} className={inputClass} />)}

      {field(t('communications.branding.logoUrl'), t('communications.branding.logoUrlHint'),
        <input value={branding.logoUrl} onChange={event => update('logoUrl', event.target.value)}
          placeholder="https://" className={inputClass} />)}

      <div className="rounded-xl border border-gray-200 bg-white p-3">
        <span className="text-xs font-medium text-gray-600">{t('communications.branding.accent')}</span>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {PALETTE.map(color => <button key={color} onClick={() => update('accentColor', color)}
            style={{ background: color }} aria-label={color}
            className={`h-7 w-7 rounded-full ring-offset-2 ${branding.accentColor === color ? 'ring-2 ring-gray-800' : ''}`} />)}
          <input type="color" value={branding.accentColor} onChange={event => update('accentColor', event.target.value)}
            className="h-7 w-10 cursor-pointer rounded border border-gray-300" />
          <code className="text-[11px] text-gray-500">{branding.accentColor}</code>
        </div>
        <span className="mt-2 block text-[10px] text-gray-500">{t('communications.branding.accentHint')}</span>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-3">
        <span className="text-xs font-medium text-gray-600">{t('communications.branding.theme')}</span>
        <div className="mt-2 flex gap-2">
          {(['LIGHT', 'DARK'] as const).map(theme => <button key={theme} onClick={() => update('theme', theme)}
            className={`rounded-lg border px-3 py-1.5 text-[11px] font-semibold ${
              branding.theme === theme ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600'
            }`}>{t(`communications.branding.themes.${theme}`)}</button>)}
        </div>
        <span className="mt-2 block text-[10px] text-gray-500">{t('communications.branding.themeHint')}</span>
      </div>

      {field(t('communications.branding.supportEmail'), t('communications.branding.supportEmailHint'),
        <input type="email" value={branding.supportEmail} onChange={event => update('supportEmail', event.target.value)}
          className={inputClass} />)}

      {field(t('communications.branding.footerNote'), t('communications.branding.footerNoteHint'),
        <textarea rows={2} value={branding.footerNote} onChange={event => update('footerNote', event.target.value)}
          className={inputClass} />)}

      {field(t('communications.branding.postalAddress'), t('communications.branding.postalAddressHint'),
        <input value={branding.postalAddress} onChange={event => update('postalAddress', event.target.value)}
          className={inputClass} />)}

      <div className="flex justify-end">
        <button onClick={save} disabled={saving}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-xs font-semibold text-white disabled:opacity-50">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {t('communications.branding.save')}
        </button>
      </div>
    </div>

    <section className="rounded-xl border border-gray-200 bg-white p-3">
      <div className="text-[10px] font-bold uppercase text-gray-400">{t('communications.branding.preview')}</div>
      <div className="mt-2 overflow-hidden rounded-lg border border-gray-200">
        <iframe title={t('communications.branding.preview')} srcDoc={preview?.html || ''} sandbox=""
          className="h-[620px] w-full bg-white" />
      </div>
    </section>
  </div>
}

export default MailBrandingPanel
